/** Hermes 写入确认卡：按目标分类 + 预览文本解析（见 2026-08-04 write-confirm-preview spec）。 */

export type WriteConfirmKind =
  | "kb_page"
  | "seed_fields"
  | "create_brand"
  | "kb_delete"
  | "journey_upsert"
  | "brand_site_release_rollback"
  | "generic";

/** 确认卡视觉风险档：新建最安全（普通色）→ 更新（提示色）→ 删除（红色）。 */
export type WriteConfirmTone = "create" | "update" | "delete";

const FIELD_LABELS: Record<string, string> = {
  legal_name: "企业全称",
  short_name: "企业简称",
  aliases: "别称 / 曾用名",
  founded_at: "成立时间",
  headquarters: "所在地",
  registered_capital: "注册资本",
  customer_segments: "客户群体",
  service_scope: "服务范围",
  core_products: "核心产品 / 服务",
  contact_phone: "联系电话",
  website: "官网",
  positioning: "品牌定位",
};

export function fieldLabelForKey(key: string): string {
  return FIELD_LABELS[key] ?? key;
}

export function classifyWriteConfirmKind(
  action: string | undefined,
  payload?: Record<string, unknown> | null,
): WriteConfirmKind {
  const a = (action ?? "").trim();
  // action 优先
  if (a === "journey_upsert") return "journey_upsert";
  if (a === "brand_site_release_rollback") return "brand_site_release_rollback";
  if (a === "create_brand") return "create_brand";
  if (
    a === "kb_vfs_rm" ||
    a === "kb_rm" ||
    a === "page_delete" ||
    a === "delete"
  ) {
    return "kb_delete";
  }
  if (a === "kb_vfs_write" || a === "kb_vfs_edit" || a === "page_upsert") return "kb_page";
  if (a === "seed_update") return "seed_fields";
  if (payload?.structured && typeof payload.structured === "object") return "seed_fields";
  return "generic";
}

/**
 * 按风险映射色调：
 * - create：新建页面 / 建品牌（安全系数最高 → 普通色）
 * - update：覆盖页面 / 改种子字段
 * - delete：删除
 */
export function writeConfirmTone(
  kind: WriteConfirmKind,
  payload?: Record<string, unknown> | null,
): WriteConfirmTone {
  if (kind === "kb_delete") return "delete";
  if (kind === "create_brand") return "create";
  if (kind === "brand_site_release_rollback") return "update";
  if (kind === "journey_upsert") {
    return journeyIdFromPayload(payload) != null ? "update" : "create";
  }
  if (kind === "kb_page" && isKbPageCreate(payload)) return "create";
  if (kind === "kb_page" || kind === "seed_fields") return "update";
  return "update";
}

/** 待确认卡片边框/底色；已处理态统一灰显。 */
export function writeConfirmToneCardClass(
  tone: WriteConfirmTone,
  options?: { decided?: boolean; interactive?: boolean },
): string {
  if (options?.decided) {
    return "border-border/60 bg-muted/30 opacity-90";
  }
  const hover = options?.interactive ? " cursor-pointer transition-colors" : "";
  switch (tone) {
    case "delete":
      return `border-destructive/40 bg-destructive/5${hover ? " hover:bg-destructive/10" : ""}`;
    case "update":
      return `border-amber-500/35 bg-amber-500/5${hover ? " hover:bg-amber-500/10" : ""}`;
    case "create":
    default:
      return `border-border/70 bg-muted/25${hover ? " hover:bg-muted/40" : ""}`;
  }
}

export function writeConfirmCardTitle(kind: WriteConfirmKind): string {
  switch (kind) {
    case "kb_page":
      return "待确认：写入知识库页面";
    case "seed_fields":
      return "待确认：更新种子字段";
    case "create_brand":
      return "确认创建品牌";
    case "kb_delete":
      return "待确认：删除知识库内容";
    case "journey_upsert":
      return "待确认：写入旅程手册";
    case "brand_site_release_rollback":
      return "待确认：回滚正式版";
    default:
      return "待确认的知识库写入";
  }
}

export type WriteConfirmSeedField = {
  key: string;
  label: string;
  value: string;
};

export function structuredFieldEntries(
  payload: Record<string, unknown> | null | undefined,
): WriteConfirmSeedField[] {
  const structured = payload?.structured;
  if (!structured || typeof structured !== "object") return [];
  return Object.entries(structured as Record<string, unknown>).map(([key, value]) => ({
    key,
    label: fieldLabelForKey(key),
    value: value == null ? "" : String(value),
  }));
}

export type MarkdownFrontmatterRow = { key: string; value: string };

export type MarkdownDocumentParts = {
  frontmatterRows: MarkdownFrontmatterRow[];
  body: string;
};

/** 拆分文档开头 YAML frontmatter 与正文（供预览表格渲染）。 */
export function splitMarkdownFrontmatter(markdown: string): MarkdownDocumentParts {
  const text = markdown.replace(/^\uFEFF/, "");
  const match = text.match(/^---\r?\n([\s\S]*?)\r?\n---(?:\r?\n|$)([\s\S]*)$/);
  if (!match) {
    return { frontmatterRows: [], body: text };
  }
  return {
    frontmatterRows: parseSimpleYamlRows(match[1] ?? ""),
    body: match[2] ?? "",
  };
}

function parseSimpleYamlRows(yaml: string): MarkdownFrontmatterRow[] {
  const rows: MarkdownFrontmatterRow[] = [];
  for (const line of yaml.split(/\r?\n/)) {
    if (!line.trim() || line.trimStart().startsWith("#")) continue;
    // 缩进续行（嵌套块）本轮忽略，避免误解析
    if (/^\s/.test(line)) continue;
    const matched = line.match(/^([A-Za-z0-9_-]+):\s*(.*)$/);
    if (!matched) continue;
    let value = matched[2].trim();
    if (
      (value.startsWith("'") && value.endsWith("'")) ||
      (value.startsWith('"') && value.endsWith('"'))
    ) {
      value = value.slice(1, -1);
    }
    rows.push({ key: matched[1], value: value.length > 0 ? value : "（空）" });
  }
  return rows;
}

/** 从 frontmatter / payload 取可读页面标题。 */
export function kbPageTitleFromPayload(
  payload: Record<string, unknown> | null | undefined,
): string | null {
  if (!payload) return null;
  if (typeof payload.title === "string" && payload.title.trim()) return payload.title.trim();

  const markdown =
    (typeof payload.after_text === "string" && payload.after_text) ||
    (typeof payload.content === "string" && payload.content) ||
    "";
  const titleRow = splitMarkdownFrontmatter(markdown).frontmatterRows.find(
    (row) => row.key === "title",
  );
  if (titleRow?.value && titleRow.value !== "（空）") return titleRow.value;
  if (typeof payload.slug === "string" && payload.slug.trim()) return payload.slug.trim();
  return null;
}

export function kbPagePathFromPayload(
  payload: Record<string, unknown> | null | undefined,
): string | null {
  if (!payload) return null;
  if (typeof payload.path === "string" && payload.path.trim()) return payload.path.trim();
  const wikiId = payload.wiki_id;
  const slug = typeof payload.slug === "string" ? payload.slug.trim() : "";
  if (wikiId != null && slug) return `wiki/${wikiId}/pages/${slug}.md`;
  return null;
}

export function isKbPageCreate(payload: Record<string, unknown> | null | undefined): boolean {
  return payload?.existing === false;
}

/** 预览用「写入后」Markdown（优先 after_text / content / body）。 */
export function resolveAfterMarkdown(
  payload: Record<string, unknown> | null | undefined,
  afterText?: string | null,
): string {
  if (typeof afterText === "string" && afterText.trim()) return afterText;
  if (!payload) return "";
  if (typeof payload.after_text === "string" && payload.after_text.trim()) {
    return payload.after_text;
  }
  if (typeof payload.content === "string" && payload.content.trim()) return payload.content;
  if (typeof payload.body === "string" && payload.body.trim()) return payload.body;
  return "";
}

export function resolveBeforeMarkdown(
  payload: Record<string, unknown> | null | undefined,
  beforeText?: string | null,
): string {
  if (typeof beforeText === "string" && beforeText.trim()) return beforeText;
  if (!payload) return "";
  if (typeof payload.before_text === "string" && payload.before_text.trim()) {
    return payload.before_text;
  }
  return "";
}

export function writeConfirmSupportsPreview(kind: WriteConfirmKind): boolean {
  return (
    kind === "kb_page" ||
    kind === "seed_fields" ||
    kind === "kb_delete" ||
    kind === "journey_upsert" ||
    kind === "brand_site_release_rollback" ||
    kind === "generic"
  );
}

/** 正式版回滚提案摘要：第 N → 第 M */
export function brandSiteRollbackSummary(
  payload: Record<string, unknown> | null | undefined,
): { fromNumber: number | null; toNumber: number | null; text: string } {
  const fromNumber = numberOrNull(payload?.from_number);
  const toNumber = numberOrNull(payload?.to_number);
  const text =
    fromNumber != null && toNumber != null
      ? `正式版第 ${fromNumber} 版 → 第 ${toNumber} 版`
      : "回滚到历史正式版";
  return { fromNumber, toNumber, text };
}

function numberOrNull(raw: unknown): number | null {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** 提案 payload 中的旅程 id（新建为 null）。 */
export function journeyIdFromPayload(
  payload: Record<string, unknown> | null | undefined,
): number | null {
  if (!payload) return null;
  const raw = payload.journey_id;
  if (raw == null || raw === "" || raw === "null" || raw === "new") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function brandIdFromPayload(
  payload: Record<string, unknown> | null | undefined,
): number | null {
  if (!payload) return null;
  const n = Number(payload.brand_id);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export type JourneyNodeSummary = {
  input_mode?: "form" | "freeform" | string;
  offset_days: number;
  /** 按模式投影后的摘要正文 */
  summary: string;
  instruction?: string;
  prompt?: string;
  send_window_start?: string;
  send_window_end?: string;
};

export function journeyNodesFromPayload(
  payload: Record<string, unknown> | null | undefined,
): JourneyNodeSummary[] {
  if (!payload || !Array.isArray(payload.nodes)) return [];
  return payload.nodes
    .filter((n): n is Record<string, unknown> => !!n && typeof n === "object")
    .map((n) => {
      const inputMode = typeof n.input_mode === "string" ? n.input_mode : "form";
      const instruction =
        typeof n.instruction === "string"
          ? n.instruction
          : typeof n.body === "string"
            ? n.body
            : "";
      const prompt = typeof n.prompt === "string" ? n.prompt : "";
      const summary = inputMode === "freeform" ? prompt : instruction;
      return {
        input_mode: inputMode,
        offset_days: Number(n.offset_days) || 0,
        summary,
        instruction,
        prompt,
        send_window_start:
          typeof n.send_window_start === "string" ? n.send_window_start : undefined,
        send_window_end: typeof n.send_window_end === "string" ? n.send_window_end : undefined,
      };
    });
}

/** 确认卡摘要用：截断节点主文案。 */
export function truncateJourneyBody(body: string, max = 48): string {
  const t = body.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max)}…`;
}

/** 预览用 Markdown：按 input_mode 投影主文案。 */
export function journeyNodesPreviewMarkdown(
  payload: Record<string, unknown> | null | undefined,
): string {
  const name = typeof payload?.name === "string" ? payload.name.trim() : "";
  const nodes = journeyNodesFromPayload(payload);
  const parts: string[] = [];
  if (name) parts.push(`# ${name}`, "");
  for (const node of nodes) {
    const modeLabel = node.input_mode === "freeform" ? "自由提示词" : "表单";
    parts.push(
      `### D+${node.offset_days}（${modeLabel}）`,
      "",
      node.summary || "（空）",
      "",
    );
  }
  return parts.join("\n").trim();
}
